import { Logo } from 'components/shared/Logo';
import { styled, Box, Typography } from '@mui/material';
import Diane from 'pages/Welcome/StudyUpdates/images/Diane.png';
import Taylor from 'pages/Welcome/StudyUpdates/images/Taylor.png';
import Courtney from 'pages/Welcome/StudyUpdates/images/Courtney.png';
import { ReactComponent as Statistics } from 'pages/Welcome/StudyUpdates/images/Statistics.svg';
import { ReactComponent as Statistics2 } from 'pages/Welcome/StudyUpdates/images/Statistics2.svg';
import {ProfileCard} from 'pages/Welcome/ResearchPaper/ProfileCard';
import { Link } from 'react-router-dom';
import { SVSOURCE_CONTACT } from 'utils/constants';


const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignContent: 'center',
}));

const StyledLogo = styled(Logo)(() => ({
  width: '300px',
}));

const StyledText = styled(Typography)(({ theme }) => ({
  color:theme.palette.primary.green,
  fontWeight: 'bold',
}));

export const researchPaperContent = {
  pageTitle: 'Study Updates',
  readMore: 'Read more',
  researchPapers: [
    {
      alt: 'Patient Journey',
      display: true,
      shouldShowTitleOnBlog: false,
      title: 'SOURCE Study Update - April 2026',
      publishedDateText: 'April 2026',
      description:
        'Since our launch in December of 2024, the growth of SOURCE has been…',
      content:(
        <>
          <LogoContainer sx={{ justifyContent: 'center' }}>
            <StyledLogo width="60%"/>
          </LogoContainer>
          <br/><br/>
          <StyledText variant='h6' sx={{ fontStyle: 'italic' }}>
            SOURCE is committed to sharing information back with our participants about our study progress.  Each quarter, we will share Study Updates with a summary of SOURCE activities and progress from the previous three-month period.
          </StyledText>
          <br/><br/>
          <Typography color='#00BC70' variant='h5'>
            SOURCE STUDY UPDATE #2
          </Typography>
          <br/>
          Since our launch in December 2024, SOURCE has continued to grow, bringing us closer to our goal of making the data available for research. We are deeply grateful to every patient and family who has generously donated data and information to make this work possible.
          <br />
          <br />
          This Study Update is packed with exciting updates from January through March 2026, including: 
          <ul>
            <li>A significant milestone for the SOURCE study and enrollment statistics</li>
            <li>Additional in-clinic recruitment sites in development</li>
            <li>The first release of data to the research community, planned for October 2026</li>
            <li>An exciting opportunity for patients and families to influence what researchers investigate</li>
          </ul>
          <br/>
          <StyledText>A SIGNIFICANT MILESTONE </StyledText>
          <br/>
          This quarter, SOURCE enrollment passed an important milestone: 1,000 participants enrolled in the study. That’s 10% of our ultimate enrollment goal. This achievement reflects the tremendous support for the study within the single ventricle community, and the commitment of patients and families to contribute to research that will ensure a healthier future for single ventricle patients.
          <br/>
          <br/>
          <strong>Why these numbers matter:</strong> With an ambitious yet realistic goal of enrolling at least 10,000 participants — 5,000 patients and 5,000 family members — SOURCE will be the world’s largest genetic and clinical dataset for single ventricle research. 
          <br/>
          <br/>
          Diseases are usually caused by a mix of genetics, environment, and chance. When researchers study a small number of patients and families, those factors are hard to separate. By studying data from many patients and their family members, researchers can identify patterns to understand the role of genetics, which genetic differences are inherited (run in families), and other factors that increase the risk of developing a single ventricle heart defect or the risk of developing related complications like heart failure, arrythmia, and liver disease.
          <br/>
          <br/>
          <strong>Why studying genetics is important:</strong> Despite decades of research, most cases of single ventricle heart defects still lack a known genetic cause. We also don’t yet understand how genetics contributes to the many complications that patients experience. SOURCE is designed to give researchers the data they need to make these discoveries. With this type of information:
          <ul>
            <li>Researchers could develop new treatments customized to a patient’s specific genetics and biology.</li>
            <li>Doctors could predict which patients are more likely to develop certain complications and intervene before those complications start.</li>
            <li>Families may better understand the likelihood of single ventricle or another congenital heart defect happening again in their family.</li>
          </ul>
          <br/>
          <StyledText>UPDATED ENROLLMENT STATISTICS THROUGH MARCH 2026</StyledText>
          <ul>
            <li>
              <strong>1000+</strong> total participants have enrolled in SOURCE. Of these:
              <ul>
                <li><strong>525+</strong> are patients, and <strong>475+</strong> are family members</li>
                <li><strong>615+</strong> have enrolled at our first in-clinic recruitment site, Boston Children’s Hospital</li>
                <li><strong>385+</strong> have enrolled online, directly on the <Link to='http://www.svsource.org' rel='noreferrer' target='_blank' style={{ textDecoration: 'underline', color: '#005487' }}>SOURCE website</Link></li>
              </ul>
            </li>
            <li><strong>44 out of 50 </strong>states are represented in the SOURCE dataset</li>
            <li><strong>10+</strong> single ventricle diagnoses are represented in the SOURCE dataset</li>
          </ul>
          <Statistics2 width='100%'/>
          <br/>
          <br/>
          <StyledText>ADDITIONAL IN-CLINIC RECRUITMENT SITES</StyledText>
          <br/>
          Patients and families can enroll in SOURCE through our <Link to='http://www.svsource.org' rel='noreferrer' target='_blank' style={{ textDecoration: 'underline', color: '#005487' }}>online portal</Link> or in-clinic at Boston Children’s Hospital. In-clinic recruitment has gone incredibly well, with a conversion rate of more than 60%. This means that more than 60% of people who are asked to join the study say “yes” and consent to participate.
          <br/>
          <br/>
          Because recruitment at Boston Children’s Hospital has been so successful, SOURCE will be expanding to many more clinics this year. As of the end of March 2026, nine additional hospitals are coming on board as in-clinic recruitment sites. Stay tuned for an updated list of in-clinic recruitment partner sites in the next Study Update.
          <br/>
          <br/>
          <StyledText>PREPARING FOR THE FIRST DATA RELEASE IN OCTOBER</StyledText>
          <br/>
          Since the launch of SOURCE recruitment in December 2024, we have been incredibly excited to see the growth in enrollment. But recruiting participants to join the study is only the first step, with the ultimate goal to make the data available for research to fuel important discoveries. This October, SOURCE will officially make the first version of the dataset available to researchers. We’ll continue to update the dataset for researchers as SOURCE recruitment continues.
          <br/>
          <br/>
          <StyledText>AN INVITATION TO SOURCE PARTICIPANTS: SHARE YOUR RESEARCH QUESTIONS </StyledText>
          <br/>
          The goal of SOURCE is to understand how genetics contributes to the cause of single ventricle heart defects as well as the many related complications that affect patients – like heart failure, protein-losing enteropathy (PLE), arrhythmia, and plastic bronchitis. Many different types of researchers will use the SOURCE dataset to ask an array of related questions, but it is equally important that the questions most meaningful to patients and families inform this work.
          <br/>
          <br/>
          With this in mind, SOURCE is asking all who are enrolled: What questions do you want answered?  What answers are most important to you? Some of the questions that might be priorities to patients and families could include things like:
          <ul>
            <li>Are there any genetic factors that make a patient more or less likely to develop heart failure?</li>
            <li>What makes some patients more resilient than others?</li>
            <li>Why do single ventricle heart defects occur twice in some families?</li>
          </ul>
          <br/>
          <strong>Our ask:</strong> We ask all SOURCE participants to submit their research questions to the study.
          <br/>
          <br/>
          <strong>What we will do with participant research questions:</strong> We will share these questions with the research community and encourage them to investigate the topics that matter most to single ventricle patients and families.
          <br/>
          <br/>
          <strong>How answers will be shared with participants:</strong> We’ll use future Study Updates to share back the questions patients and families are asking, which questions researchers are investigating, and the answers they find.
          <br/>
          <br/>
          <strong>How to submit research questions:</strong> Click <Link to='https://docs.google.com/forms/d/e/1FAIpQLScNWlEKzUVoO8fhGxdqm3cbljHIi5VFuqweqCO38VBu-RRLNg/viewform?usp=header' target='_blank' rel='noreferrer' style={{ textDecoration: 'underline', color: '#005487' }}>HERE</Link> to share your ideas. You may submit anonymously if you wish.
          <br/>
          <br/>
          <em>Please note that we only share de-identified data with researchers, which means that they will not be able to investigate a question specific to an individual or family.</em>
          <br/>
          <br/>
          <Typography 
            variant="h6" 
            sx={{ fontWeight: 'bold', color: '#00BC70' }}
          >
            Thank you!
          </Typography>
          <br/>
          We are grateful to every one of our SOURCE study participants for contributing to a brighter future for all those who live with single ventricle heart defects.
          <br/>
          <br/>
          With hope and appreciation,
          <br/>
          <br/>
          The SOURCE Team at Additional Ventures
        </>
      )
    },
    {
      alt: 'Patient Journey',
      display: true,
      shouldShowTitleOnBlog: false,
      title: 'SOURCE Study Update - December 2025',
      publishedDateText: 'December 2025',
      description:
        'Welcome to our first SOURCE study update! Since our launch one year ago…',
      content:(
        <>
          <br/><br/>
          <LogoContainer>
            <StyledLogo/>
          </LogoContainer>
          <br/><br/>
          <StyledText variant='h6' sx={{ fontStyle: 'italic' }}>
            SOURCE is committed to sharing information back with our participants about our study progress.  Each quarter, we will share Study Updates with a summary of SOURCE activities and progress from the previous three-month period.
          </StyledText>
          <br/><br/>
          <StyledText variant='h5'>
            SOURCE STUDY UPDATE #1
          </StyledText>
          <br/>
          Welcome to our first SOURCE study update!
          <br />
          <br />
          Since our launch one year ago, SOURCE has enrolled more than 700 individuals into our study. Each one of these individuals – patients, parents, siblings, and children of single ventricle patients – has generously donated their genetic and clinical information in the shared belief that research holds hope for better treatments, outcomes, and quality of life with single ventricle heart disease.
          <br />
          <br />
          While researchers’ focus will be digging deeply into the genetic and clinical data in the SOURCE dataset, we see more than just data. We see:
          <ul>
            <li>700+ personal stories of the impact of single ventricle.</li>
            <li>700+ important reasons to participate in single ventricle research.</li>
            <li>700+ helpful voices, urging researchers to move quickly, to make the discoveries needed to unlock new treatments and, ultimately, cures for single ventricle.</li>
          </ul>
          <br />
          It has been a year of hard work, tremendous progress, and proof that the single ventricle patient and family community enthusiastically supports research to unlock the mysteries of single ventricle genetics and biology. We are just getting started and can’t be more appreciative of each and every patient and family member who has joined the SOURCE study.
          <br />
          <br />
          We will keep these study updates coming to keep you aware of our study progress, the research underway, and the discoveries made possible through your participation in SOURCE.
          <br />
          <br />
          <br/>
          <StyledText>STUDY PROGRESS TO DATE</StyledText>
          <br/>
          We are enrolling participants online through <Link to='http://www.svsource.org' rel='noreferrer' target='_blank'>svsource.org</Link> and in participating clinics. In April, we launched our first in-clinic recruitment site at Boston Children’s Hospital.
          <br/>
          <br/>
          Below is a snapshot of our enrollment statistics to date:
          <br/>
          <br/>
          <Statistics width='100%'/>
          <br/>
          <br/>
          In 2026, we anticipate adding at least 5-7 additional cardiology centers as SOURCE recruitment sites.  Additionally, we are developing partnerships with several institutions that have their own genetic studies to enable sample sharing into SOURCE.  This will help us grow the SOURCE dataset much more quickly.
          <br/>
          <br/>
          <StyledText>SCIENCE SPOTLIGHT</StyledText>
          <br/>
          We are very excited about our planned launch of the SOURCE dataset – what we call reSOURCE – in October 2026.  This will be a pivotal time for SOURCE as it marks the start of research made possible through the study.  As research gets underway, we look forward to sharing information about the research questions being explored and discoveries that are made.
          <br/>
          <br/>
          The genetic and clinical data in SOURCE will allow researchers to:
          <ul>
            <li>Find genes or molecular pathways that increase risk of single ventricle heart defects.</li>
            <li>Predict which patients are at risk of certain complications.</li>
            <li>Develop new treatments that target specific complications.</li>
            <li>Discover biological markers that predict which treatments may work best for which patients.</li>
            <li>Develop ways to tailor care and treatments to a patient's specific genetics and biology.</li>
            <li>Maybe even develop cures for single ventricle.</li>
          </ul>
          <br/>
          This approach has already transformed other areas of medicine. For example, large-scale genetic studies in cardiomyopathies (heart muscle disease) and arrhythmias have helped identify genes that cause disease, improve early diagnosis, and guide treatment that matches a person’s genes. Similar breakthroughs have occurred in cancer, where understanding tumor genetics has led to personalized therapies that dramatically improve outcomes. We believe this kind of transformative change is possible in single ventricle as well.
          <br/>
          <br/>
          <Typography 
            variant="h6" 
            sx={{ fontWeight: 'bold' }}
          >
            Progress so far:
          </Typography>
          <br/>
          The SOURCE team has been carefully collecting and processing genetic and clinical data to make sure it is complete, accurate, and de-identified (so that no participant can be identified). DNA samples from participants are being sequenced, which lets researchers study the genes within the DNA and begin answering important questions about single ventricle heart defects – such as which genes may increase risk, who may be more likely to develop certain complications, and which treatments may work best for which patients.
          <br/>
          <br/>
          So far, we have sequenced over 500 samples from SOURCE participants, including 259 samples from individuals with single ventricle (132 adults and 127 minors) and 247 samples from their family members.  Patients represent all single ventricle diagnoses, with hypoplastic left heart syndrome the most common (36%), followed by tricuspid atresia (13%) and hypoplastic right heart (11%).
          <br/>
          <br/>
          <Typography 
            variant="h6" 
            sx={{ fontWeight: 'bold' }}
          >
            Secondary Genetic Results:
          </Typography>
          <br/>
          As explained during the consent process, we are unable to return primary genetic results to SOURCE participants – findings about specific genetic causes of single ventricle.  This is because we do not yet know which genes or variants cause the disease.  However, we are able to return “actionable secondary results” to participants who opt in during the consent process.  These results are not related to single ventricle but are linked to other health conditions that have screening, prevention, or treatment recommendations.
          <br/>
          <br/>
          As of August 2025, 22 of the 500 sequenced SOURCE participants (about 8%) were found to have an actionable secondary result, including both single ventricle patients and family members. Nearly half of these findings occurred in families where more than one participant had the same finding.  Approximately half were related to cardiovascular conditions (not single ventricle). Interestingly, this 8% incidence is higher than what is seen in the general population, which is 1-3%.  While we do not yet understand the significance of this finding, it is one of the questions researchers will dig into when reSOURCE launches.
          <br/>
          <br/>
          <StyledText>MEET THE SOURCE TEAM</StyledText>
          <br />
          <br />
          <ProfileCard 
            image={Diane} 
            name="Diane M. Pickles" 
            title="Director"
          >
            <span className="quote">
              "As the mother of an adult with HLHS, I know first-hand how desperately single ventricle patients and their families need new treatments that will enable longer, healthier lives. The SOURCE study gives me great hope for the future for all single ventricle patients, those alive today and those born in the future. It is a true honor to direct this ground-breaking study."
            </span>
          </ProfileCard>

          <ProfileCard 
            image={Taylor} 
            name="Taylor MacLean" 
            title="Program Manager"
          >
            Oversees all participant recruitment, support and communications.
            <br />
            <br />
            <span className="quote">
              "If you’ve reached out to SOURCE with any questions or support needs, we have most likely already ‘met’! My favorite part of my job is the opportunity to talk and connect with patients and their families who have so generously shared their most valuable data and information with us. We are so grateful to be working toward the shared mission of SOURCE with all of you!"
            </span>
          </ProfileCard>
          <ProfileCard 
            image={Courtney} 
            name="Courtney Steger" 
            title="PhD, Program Manager"
          >
            Oversees data analysis processes and researcher access to the dataset.
            <br />
            <br />
            <span className="quote">
              "I’m inspired every day by the willingness of SOURCE participants to share their data and experiences. Each contribution, each sequence, each clinical record helps us build a dataset of unprecedented depth – one that will finally give researchers the tools to pursue questions that have remained out of reach for far too long. I’m honored to help shape this reSOURCE and be part of the effort that will move the field forward."
            </span>
          </ProfileCard>
          <StyledText>HOW YOU CAN SUPPORT SOURCE</StyledText>
          <br/>
          Whether you are a single ventricle patient or family member, a clinician working with single ventricle patients, or a researcher, there are many ways you can get support SOURCE.
          <ul>
            <li><strong>Spread the word about SOURCE to eligible patients and family members living in the US.</strong> This includes all single ventricle patients (all ages and stages, including post-transplant) and their immediate biological family members. They can enroll at <Link to='http://www.svsource.org' rel='noreferrer' target='_blank'>svsource.org</Link>.</li>
            <li><strong>Tell us why you think SOURCE is important!</strong> We would love to share your thoughts in a future study update or social media post.  Click <Link to='https://docs.google.com/forms/d/e/1FAIpQLScNWlEKzUVoO8fhGxdqm3cbljHIi5VFuqweqCO38VBu-RRLNg/viewform?usp=header' target='_blank' rel='noreferrer'>HERE</Link> to share your comments.</li>
            <li><strong>Join SOURCE as an in-clinic recruitment site.</strong> If you are a clinician interested in enrolling patients and families into SOURCE in your clinic, please reach out to Diane Pickles at <a href={`mailto:dpickles@additionalventures.org`}>dpickles@additionalventures.org</a></li>
            <li><strong>Advance scientific research using SOURCE data.</strong> If you are a researcher, stay tuned for our first data release in <strong>October 2026.</strong></li>
            <li><strong>Share this study update with others!</strong></li>
            <li><strong>Let us know if you have any questions or suggestions,</strong> by emailing us at <a href={`mailto:${SVSOURCE_CONTACT.EMAIL}`}>{SVSOURCE_CONTACT.EMAIL}</a>.</li>
          </ul>
          <br/>
          With hope and appreciation,
          <br/>
          The SOURCE Team at Additional Ventures
        </>
      )
    }
  ],
};
